import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Fondateur : crée un lien temporaire vers l'agent vocal de démonstration (séparé du sien, sans accès à ses dossiers). */
export default function LienDemoVoix() {
  const [label, setLabel] = useState("");
  const [heures, setHeures] = useState(3);
  const [seances, setSeances] = useState(6);
  const [lien, setLien] = useState<{ url: string; expire_a: string; seances_max: number } | null>(null);
  const [occupe, setOccupe] = useState(false);

  const creer = async () => {
    setOccupe(true);
    try {
      const r = await fetch(`${API_URL}/api/voice/demo-lien`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ label, heures, seances }) });
      if (r.ok) setLien(await r.json());
      else toast.error("Création du lien impossible");
    } catch {
      toast.error("Création du lien impossible");
    } finally {
      setOccupe(false);
    }
  };

  const copier = async () => {
    if (!lien) return;
    try { await navigator.clipboard.writeText(lien.url); toast.success("Lien copié"); } catch { toast.error("Copie impossible sur cet appareil"); }
  };

  return (
    <Card data-testid="lien-demo-voix">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Faire écouter Grafton à un tiers</CardTitle>
        </div>
        <CardDescription>Crée un lien temporaire vers un agent de démonstration, séparé du vôtre : il ne connaît aucune de vos données.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <input className="border rounded-md bg-background px-3 h-9 text-sm w-full" placeholder="Pour qui ? (facultatif, ex. Pâte à Pizza)" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
        <div className="flex flex-wrap gap-2 text-sm">
          <label className="flex items-center gap-2">Valable
            <select className="border rounded-md bg-background px-2 h-9" value={heures} onChange={(e) => setHeures(Number(e.target.value))}>
              {[2, 3, 6, 12, 24].map((h) => <option key={h} value={h}>{h} h</option>)}
            </select></label>
          <label className="flex items-center gap-2">Séances
            <select className="border rounded-md bg-background px-2 h-9" value={seances} onChange={(e) => setSeances(Number(e.target.value))}>
              {[1, 3, 6, 10, 20].map((n) => <option key={n} value={n}>{n}</option>)}
            </select></label>
        </div>
        <Button onClick={creer} disabled={occupe} className="gap-2">
          {occupe ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Créer le lien
        </Button>
        {lien && (
          <div className="rounded-md bg-muted/40 p-3 space-y-2 text-sm">
            <p className="break-all font-mono text-xs">{lien.url}</p>
            <p className="text-xs text-muted-foreground">Valable jusqu'au {new Date(lien.expire_a).toLocaleString("fr-FR")} · {lien.seances_max} séance(s) · 10 minutes par conversation</p>
            <Button size="sm" variant="outline" onClick={copier} className="gap-1.5"><Copy className="h-3.5 w-3.5" /> Copier le lien</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
